/**
 * intuitiveSignalResolver.js — FASE R7.
 *
 * Priority-collapses ALL active system signals down to:
 *   - 1 dominant signal
 *   - max 1 secondary signal
 *   - everything else: ignored
 *
 * This is the "human perception" layer — a pastor doesn't process
 * 9 simultaneous dimensions. They feel the dominant weight and speak from it.
 *
 * Priority order (hardcoded, non-configurable):
 *   1. crisis (riskLevel high OR critical exhaustion)
 *   2. grief
 *   3. exhaustion (persistent pastoral exhaustion)
 *   4. conflict
 *   5. silent_tension
 *   6. continuity_concern (regression / recurring without resolution)
 *   7. spiritual_process (búsqueda / restauración in progress)
 *   8. reflection
 *   9. logistics → suppresses all leve emotional signals
 *   10. simple_presence (default when nothing significant)
 *
 * Rule: PREFER omit over over-analyze.
 */

const LOGISTIC_INTENTS = new Set(["planning", "logistics", "analysis", "events"]);

const CRISIS_EXHAUSTION = new Set([
  "saturación_crítica",
  "agotamiento_crónico",
]);

const MODERATE_EXHAUSTION = new Set([
  "agotamiento_espiritual",
  "fatiga_moderada",
]);

/**
 * @param {{
 *   emotion:            object|null,
 *   riskLevel:          string,
 *   processState:       string|null,
 *   exhaustionState:    string,
 *   conflictType:       string,
 *   continuityPattern:  string,
 *   silentTension:      number,
 *   intent:             string,
 *   capacity:           string,
 *   rhythmProfile:      string|null,
 * }} params
 * @returns {{
 *   dominantSignal:  string,
 *   secondarySignal: string|null,
 *   ignoredSignals:  string[],
 *   intensity:       "critical"|"high"|"moderate"|"low",
 * }}
 */
export function resolveIntuitiveSignals({
  emotion         = null,
  riskLevel       = "none",
  processState    = null,
  exhaustionState = "estable",
  conflictType    = "ninguno",
  continuityPattern = "sin_patron",
  silentTension   = 0,
  intent          = "general",
  capacity        = "stable",
}) {
  const emotionType = emotion?.type ?? emotion ?? "neutral";
  const signals     = [];

  // Crisis — always dominates
  if (riskLevel === "high" || CRISIS_EXHAUSTION.has(exhaustionState) || processState === "quebranto" && riskLevel !== "none") {
    signals.push({ type: "crisis", intensity: "critical" });
  }

  // Grief
  if (emotionType === "grief" || processState === "quebranto") {
    if (!signals.find((s) => s.type === "crisis")) {
      signals.push({ type: "grief", intensity: "high" });
    }
  }

  // Exhaustion (persistent)
  if (MODERATE_EXHAUSTION.has(exhaustionState) || processState === "agotamiento_espiritual" || capacity === "overwhelmed" || capacity === "depleted") {
    if (signals.length < 2) signals.push({ type: "exhaustion", intensity: "high" });
  }

  // Conflict
  if (conflictType && conflictType !== "ninguno") {
    if (signals.length < 2) signals.push({ type: "conflict", intensity: "moderate" });
  }

  // Silent tension
  if (silentTension >= 2) {
    if (signals.length < 2) signals.push({ type: "silent_tension", intensity: "moderate" });
  }

  // Continuity concern (recurring grief/exhaustion without resolution)
  const concernPatterns = ["recurrencia_sin_resolucion", "recaida_despues_de_restauracion", "estancamiento"];
  if (concernPatterns.includes(continuityPattern)) {
    if (signals.length < 2) signals.push({ type: "continuity_concern", intensity: "moderate" });
  }

  // Spiritual process
  if (processState === "búsqueda" || processState === "restauración") {
    if (signals.length < 2) signals.push({ type: "spiritual_process", intensity: "moderate" });
  }

  // Reflection
  if (emotionType === "reflective" || emotionType === "deep-focus") {
    if (signals.length < 2) signals.push({ type: "reflection", intensity: "low" });
  }

  // Logistics: suppress leve emotional signals — only keep crisis
  if (LOGISTIC_INTENTS.has(intent)) {
    const critical = signals.filter((s) => s.intensity === "critical");
    if (critical.length === 0) {
      return {
        dominantSignal:  "logistics",
        secondarySignal: null,
        ignoredSignals:  signals.map((s) => s.type),
        intensity:       "low",
      };
    }
    // Keep only the crisis signal
    signals.splice(0, signals.length, ...critical);
  }

  if (signals.length === 0) {
    return { dominantSignal: "simple_presence", secondarySignal: null, ignoredSignals: [], intensity: "low" };
  }

  return {
    dominantSignal:  signals[0].type,
    secondarySignal: signals[1]?.type ?? null,
    ignoredSignals:  signals.slice(2).map((s) => s.type),
    intensity:       signals[0].intensity,
  };
}
